import { useEffect, useState } from 'react';
import { Icona } from '../../components/Icona';
import { subappalti } from '../../api/subappalti';
import type { DettaglioSubappalto } from '../../api/subappalti';
import { apri } from '../../lib/ancora';
import { formattaData, formattaImporto } from '../../lib/formato';
import { numeroDecimale } from '../../lib/numeri';
import { usePuo } from '../sessione/ContestoSessione';
import { ChiediMotivo } from './ChiediMotivo';

/*  LA SCHEDA DEL SUBAPPALTO. In bozza si corregge tutto; confermato — contratto firmato —
    solo le note. Dice da quale commessa e da quale voce del preventivo viene, e quanto si
    era preventivato: e' il confronto che serve prima di firmare.  */
export function SchedaSubappalto({
  subappaltoId,
  onChiudi,
  onCambiato,
}: {
  subappaltoId: string;
  onChiudi: () => void;
  onCambiato: () => void;
}) {
  const puoFare = usePuo();
  const [s, setS] = useState<DettaglioSubappalto | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);
  const [annullando, setAnnullando] = useState(false);

  const [descrizione, setDescrizione] = useState('');
  const [importo, setImporto] = useState('');
  const [inizio, setInizio] = useState('');
  const [fine, setFine] = useState('');
  const [note, setNote] = useState('');

  function allinea(d: DettaglioSubappalto) {
    setS(d);
    setDescrizione(d.descrizione);
    setImporto(String(d.importo));
    setInizio(d.dataInizio ?? '');
    setFine(d.dataFine ?? '');
    setNote(d.note ?? '');
  }

  useEffect(() => {
    let annullato = false;
    setS(null);
    subappalti
      .leggi(subappaltoId)
      .then((d) => !annullato && allinea(d))
      .catch((e: Error) => !annullato && setErrore(e.message));
    return () => {
      annullato = true;
    };
  }, [subappaltoId]);

  async function agisci(azione: () => Promise<DettaglioSubappalto>) {
    setInCorso(true);
    setErrore(null);
    try {
      allinea(await azione());
      onCambiato();
    } catch (e) {
      setErrore((e as Error).message);
    } finally {
      setInCorso(false);
    }
  }

  if (!s) {
    return (
      <aside className="scheda">
        {errore ? <p className="errore">{errore}</p> : <p className="attesa">Carico il subappalto…</p>}
      </aside>
    );
  }

  const puoScrivere = puoFare('EmettiOrdini');
  const inBozza = s.stato === 'Bozza';
  const valore = numeroDecimale(importo);
  const cambiato =
    descrizione !== s.descrizione ||
    valore !== s.importo ||
    inizio !== (s.dataInizio ?? '') ||
    fine !== (s.dataFine ?? '') ||
    note !== (s.note ?? '');

  const salva = () =>
    agisci(() =>
      subappalti.correggi(s.id, {
        codiceFornitoreMago: s.codiceFornitoreMago,
        fornitore: s.fornitore,
        descrizione: descrizione.trim(),
        importo: valore ?? 0,
        dataInizio: inizio === '' ? null : inizio,
        dataFine: fine === '' ? null : fine,
        note: note.trim() === '' ? null : note.trim(),
      }),
    );

  const scarto = s.preventivato !== null ? s.importo - s.preventivato : null;

  return (
    <aside className="scheda subappalto" aria-label={`Subappalto ${s.numero ?? ''}`}>
      <header>
        <div>
          <p className="chiave">
            <strong>{s.numero ?? 'senza numero'}</strong>
            <span className={`stato stato-${s.stato.toLowerCase()}`}>{s.stato}</span>
          </p>
          <h2>{s.fornitore ?? s.codiceFornitoreMago}</h2>
          <p className="riferimenti">
            <button type="button" className="collegamento" onClick={() => apri('commesse', s.commessaId)}>
              commessa {s.commessa}
            </button>
            {s.cliente && ` · ${s.cliente}`}
            {s.preventivoId && (
              <>
                {' · offerta '}
                <button type="button" className="collegamento" onClick={() => apri('preventivi', s.preventivoId!)}>
                  {s.preventivo ?? 'senza numero'}
                </button>
              </>
            )}
          </p>
        </div>
        <button type="button" className="chiudi" onClick={onChiudi} aria-label="Chiudi la scheda">
          <Icona nome="chiudi" />
        </button>
      </header>

      {s.motivoAnnullamento && <p className="avviso">Annullato: {s.motivoAnnullamento}</p>}

      {s.rigaPreventivo !== null ? (
        <p className="nota">
          Affida la voce «{s.rigaPreventivo}»
          {s.preventivato !== null && <>, preventivata {formattaImporto(s.preventivato)}</>}
          {scarto !== null && scarto !== 0 && (
            <strong className={scarto > 0 ? 'negativo' : ''}>
              {' '}
              ({scarto > 0 ? '+' : ''}
              {formattaImporto(scarto)} sul preventivato)
            </strong>
          )}
          .
        </p>
      ) : (
        <p className="nota">Scritto libero: non viene da una voce del preventivo.</p>
      )}

      <label className="campo-blocco">
        <span>Lavoro affidato</span>
        <textarea
          rows={3}
          value={descrizione}
          readOnly={!inBozza || !puoScrivere}
          onChange={(e) => setDescrizione(e.target.value)}
        />
      </label>

      <div className="campi">
        <label className="campo-blocco">
          <span>Importo</span>
          {inBozza && puoScrivere ? (
            <input inputMode="decimal" value={importo} onChange={(e) => setImporto(e.target.value)} />
          ) : (
            <strong>{formattaImporto(s.importo)}</strong>
          )}
        </label>
        <label className="campo-blocco">
          <span>Dal</span>
          {inBozza && puoScrivere ? (
            <input type="date" value={inizio} onChange={(e) => setInizio(e.target.value)} />
          ) : (
            <span>{s.dataInizio ? formattaData(s.dataInizio) : '—'}</span>
          )}
        </label>
        <label className="campo-blocco">
          <span>Al</span>
          {inBozza && puoScrivere ? (
            <input type="date" value={fine} onChange={(e) => setFine(e.target.value)} />
          ) : (
            <span>{s.dataFine ? formattaData(s.dataFine) : '—'}</span>
          )}
        </label>
      </div>

      <label className="campo-blocco">
        <span>Note</span>
        <textarea
          rows={2}
          value={note}
          readOnly={!puoScrivere || s.stato === 'Annullato'}
          onChange={(e) => setNote(e.target.value)}
        />
      </label>

      <p className="riferimenti">
        Aperto il {formattaData(s.creatoIl.slice(0, 10))} da {s.creatoDa}
        {s.confermatoIl && ` · confermato il ${formattaData(s.confermatoIl.slice(0, 10))}`}
      </p>

      {errore && <p className="errore">{errore}</p>}

      {annullando && (
        <ChiediMotivo
          domanda="Perché si annulla questo subappalto?"
          etichettaConferma="Annulla il subappalto"
          inCorso={inCorso}
          onConferma={(motivo) => {
            setAnnullando(false);
            void agisci(() => subappalti.annulla(s.id, motivo));
          }}
          onAnnulla={() => setAnnullando(false)}
        />
      )}

      <div className="azioni">
        {puoScrivere && cambiato && s.stato !== 'Annullato' && (
          <button type="button" className="primario" disabled={inCorso || valore === null} onClick={() => void salva()}>
            Salva
          </button>
        )}
        {puoScrivere && inBozza && !cambiato && (
          <button
            type="button"
            className="primario"
            disabled={inCorso}
            title="Contratto firmato con l'impresa: da qui l'importo non si cambia più"
            onClick={() => void agisci(() => subappalti.conferma(s.id))}
          >
            Conferma (contratto firmato)
          </button>
        )}
        {puoScrivere && s.stato === 'Confermato' && !cambiato && (
          <button type="button" disabled={inCorso} onClick={() => void agisci(() => subappalti.concludi(s.id))}>
            Lavoro concluso
          </button>
        )}
        {puoFare('AnnullaOrdini') && (s.stato === 'Bozza' || s.stato === 'Confermato') && (
          <button type="button" className="pericolo" disabled={inCorso} onClick={() => setAnnullando(true)}>
            Annulla il subappalto
          </button>
        )}
      </div>
    </aside>
  );
}
